import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Star, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/journey")({
  head: () => pageHead({
    path: "/journey",
    title: "Your reading journey — Book Swap",
    description: "Track the books you're reading, finished and rated, with notes you can keep private on your Book Swap journey.",
  }),
  component: Journey,
});

type Entry = {
  id: string; book_id: string; status: string; rating: number | null; notes: string | null;
  finished_at: string | null; created_at: string;
  books?: { title: string; author: string; cover_url: string | null } | null;
};

function Journey() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("reading_journey")
      .select("id,book_id,status,rating,notes,finished_at,created_at,books(title,author,cover_url)")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    setEntries((data as any) ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  async function update(id: string, patch: Record<string, unknown>) {
    const { error } = await supabase.from("reading_journey").update(patch as any).eq("id", id);
    if (error) toast.error("Could not update."); else load();
  }
  async function remove(id: string) {
    await supabase.from("reading_journey").delete().eq("id", id);
    setEntries((e) => e.filter((x) => x.id !== id));
  }

  if (!user) return <div className="min-h-screen bg-background"><Navbar /><p className="p-12 text-center">Sign in to track your reading.</p></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="text-3xl font-bold">My reading journey</h1>
        <p className="mt-1 text-muted-foreground">Track what you're reading, finished, or want to read.</p>

        {loading ? <p className="mt-8 text-muted-foreground">Loading…</p> : entries.length === 0 ? (
          <div className="mt-12 rounded-2xl bg-card p-10 text-center shadow-[var(--shadow-card)]">
            <BookOpen className="mx-auto h-12 w-12 text-primary/40" />
            <p className="mt-3 font-semibold">No entries yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Open any book and tap "Add to my journey" to start tracking.</p>
          </div>
        ) : (
          <ul className="mt-6 space-y-4">
            {entries.map((e) => (
              <li key={e.id} className="rounded-2xl bg-card p-5 shadow-[var(--shadow-card)]">
                <div className="flex gap-4">
                  <div className="h-24 w-16 shrink-0 overflow-hidden rounded bg-primary-soft">
                    {e.books?.cover_url ? <img src={e.books.cover_url} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><BookOpen className="h-6 w-6 text-primary/40" /></div>}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{e.books?.title ?? "Book"}</p>
                    <p className="text-sm text-muted-foreground">{e.books?.author}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {(["want_to_read", "reading", "finished"] as const).map((s) => (
                        <button key={s} onClick={() => update(e.id, { status: s, finished_at: s === "finished" ? new Date().toISOString() : null })}
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${e.status === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                          {s.replace("_", " ")}
                        </button>
                      ))}
                      <Button variant="ghost" size="sm" aria-label="Remove from reading journey" onClick={() => remove(e.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                    {e.status === "finished" && (
                      <div className="mt-3 flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} aria-label={`Rate ${n} out of 5`} onClick={() => update(e.id, { rating: n })}>
                            <Star className={`h-5 w-5 ${(e.rating ?? 0) >= n ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                          </button>
                        ))}
                      </div>
                    )}
                    <Textarea className="mt-3" placeholder="Notes / mini-review (optional)"
                      defaultValue={e.notes ?? ""} maxLength={500}
                      onBlur={(ev) => ev.target.value !== (e.notes ?? "") && update(e.id, { notes: ev.target.value })} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
